// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import CustomSearch from "../../../../components/custom-search/custom-search";
import RedemptionsService from "../../redemptions.service";
import { ReactSVG } from "react-svg";
import { WithdrawalGroups } from "../../../../utils/data/constant";

const Pending: React.FC = (): ReactElement => {
  let searchTimer = null;
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const redemptionService = new RedemptionsService();
  const [pendingRequestsList, setPendingRequestsList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchPendingListData();
  }, [searchWord, sortField, sortOrder, filters]);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      fixed: "left",
      render: (_, record) => record?.account?.activeKyc?.nameEn,
    },
    {
      title: "Client account",
      dataIndex: "accountCode",
      key: "accountCode",
      width: 130,
      sorter: true,
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.portfolio?.account?.id}/${record?.portfolio?.account?.activeKycId}/details`}
        >
          {record?.portfolio?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) => record?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "Portfolio number",
      dataIndex: "portfolioCode",
      key: "portfolioCode",
      sorter: true,
      render: (_, record) => <span>{record?.portfolio?.accountCode}</span>,
    },
    {
      title: "Redemption units",
      dataIndex: "redemptionUnit",
      key: "redemptionUnit",
      width: 180,
      sorter: true,
    },
    {
      title: "Funds name",
      dataIndex: "fundNameEn",
      key: "fundNameEn",
      width: 180,
      render: (_, record) => <span>{record?.fundNameEn || "-"}</span>,
    },
    {
      title: "Step",
      dataIndex: "step",
      key: "step",
      width: 180,
      render: (_, record) => (
        <span>{record?.withdraw?.approvalStep || "Operation"}</span>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "souce",
      render: (text) =>
        text === "Portal" ? (
          <ReactSVG src={"/images/monitor.svg"} />
        ) : (
          <ReactSVG src={"/images/mobile.svg"} />
        ),
    },
    {
      title: "Request Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text) => (
        <span className="date text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) =>
        record?.withdraw?.approvalStep === "WMD" ||
        record?.withdraw?.approvalStep === "AMD" ||
        record?.status === "PENDING_WITHDRAW" ? (
          <div className="flex gap-3">
            <ReactSVG
              src={"/images/view.svg"}
              onClick={() => {
                navigate(`/redemptions/take-action/${record.id}`);
              }}
            />
          </div>
        ) : (
          <ReactSVG
            src={"/images/take-action.svg"}
            onClick={() => {
              navigate(`/redemptions/take-action/${record.id}`);
            }}
          />
        ),
    },
  ];

  const fetchPendingListData = async (page = 1): Promise<void> => {
    const data = await redemptionService.getRedemptionListData(
      ["Pending", "PENDING_WITHDRAW"],
      page,
      searchWord,
      sortField,
      sortOrder,
      filters
    );

    if (data !== undefined) {
      setResponseDate(data?.data);
      setPendingRequestsList(data?.data?.redemptions);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilters(JSON.stringify(filters?.status));
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {pendingRequestsList?.length} Pending requests{" "}
          {user?.withdrawGroup
            ? `for ${WithdrawalGroups[user?.withdrawGroup]}`
            : ""}
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {pendingRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={pendingRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchPendingListData(page);
          }}
        />
      ) : (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      )}
    </div>
  );
};

export default Pending;
